// UserExport.js - User Export Component
import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Select,
  Button,
  Input,
  Form,
  message,
  Alert,
  Divider,
  Space,
} from "antd";
import {
  DownloadIcon,
  FileSpreadsheetIcon,
  UsersIcon,
  FilterIcon,
  FileTextIcon,
} from "lucide-react";
import { API_METHODS } from "../../../constants";
import useApi from "../../../hooks/useApi";
import * as XLSX from "xlsx";

const { Option } = Select;

function UserExport() {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [exportType, setExportType] = useState("all");

  // API hook
  const [exportResponse, exportRequest] = useApi();

  // Handle export response
  useEffect(() => {
    if (exportResponse.data && !exportResponse.isLoading) {
      handleDownload(exportResponse.data);
      messageApi.success("Export completed successfully!");
    } else if (exportResponse.error) {
      messageApi.error(exportResponse.error || "Export failed");
    }
  }, [exportResponse]);

  // Handle form submission
  const handleExport = (values) => {
    const queryParams = new URLSearchParams();

    // Add export type
    queryParams.append("export_type", exportType);

    // Add filters based on export type
    if (exportType === "filtered") {
      Object.entries(values).forEach(([key, value]) => {
        if (value && value !== "all" && value !== "") {
          queryParams.append(key, value);
        }
      });
    } else if (exportType === "single" && values.user_id) {
      queryParams.append("user_id", values.user_id);
    }

    exportRequest(
      null,
      API_METHODS.PUT,
      `/admin/users/export?${queryParams.toString()}`
    );
  };

  // Download file
  const handleDownload = (data) => {
    try {
      // Create workbook
      const workbook = XLSX.utils.book_new();

      // Convert data to worksheet
      const worksheet = XLSX.utils.json_to_sheet(data.data);

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, "Users");

      // Generate filename
      const timestamp = new Date().toISOString().split("T")[0];
      const filename = `users_export_${data.export_type}_${timestamp}.xlsx`;

      // Download file
      XLSX.writeFile(workbook, filename);
    } catch (error) {
      messageApi.error("Failed to download file");
      console.error("Export download error:", error);
    }
  };

  const exportOptions = [
    {
      value: "all",
      label: "All Users",
      description: "Export all users in the system (max 1000)",
      icon: <UsersIcon className="h-4 w-4" />,
    },
    {
      value: "filtered",
      label: "Filtered Users",
      description: "Export users based on specific filters",
      icon: <FilterIcon className="h-4 w-4" />,
    },
    {
      value: "single",
      label: "Single User",
      description: "Export data for a specific user",
      icon: <FileTextIcon className="h-4 w-4" />,
    },
  ];

  return (
    <>
      {contextHolder}
      <div>
        {/* Export Type Selection */}
        <Card title="Export User Data" className="mb-4">
          <Row gutter={16}>
            {exportOptions.map((option) => (
              <Col xs={24} sm={8} key={option.value}>
                <Card
                  size="small"
                  className={`cursor-pointer transition-all ${
                    exportType === option.value
                      ? "border-orange-500 bg-orange-50"
                      : "border-gray-200 hover:border-orange-300"
                  }`}
                  onClick={() => setExportType(option.value)}
                >
                  <div className="text-center">
                    <div className="mb-2 flex justify-center">
                      {option.icon}
                    </div>
                    <h4 className="font-semibold mb-1">{option.label}</h4>
                    <p className="text-sm text-gray-600 mb-0">
                      {option.description}
                    </p>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </Card>

        {/* Export Configuration */}
        <Card title="Export Configuration">
          <Form form={form} onFinish={handleExport} layout="vertical">
            {/* Filters for filtered export */}
            {exportType === "filtered" && (
              <>
                <Alert
                  message="Filter Options"
                  description="Configure filters to export specific users. Leave fields empty to include all."
                  type="info"
                  showIcon
                  className="mb-4"
                />

                <Row gutter={16}>
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item label="User Status" name="status">
                      <Select placeholder="Select status" allowClear>
                        <Option value="active">Active Users</Option>
                        <Option value="disabled">Disabled Users</Option>
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      label="Verification Status"
                      name="verification_status"
                    >
                      <Select placeholder="Select verification" allowClear>
                        <Option value="verified">Verified</Option>
                        <Option value="unverified">Unverified</Option>
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12} md={8}>
                    <Form.Item label="City" name="city">
                      <Input placeholder="Enter city name" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item label="State" name="state">
                      <Input placeholder="Enter state name" />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12} md={8}>
                    <Form.Item label="Search by Email" name="search_email">
                      <Input placeholder="Enter email to search" />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12} md={8}>
                    <Form.Item label="Search by Name" name="search_name">
                      <Input placeholder="Enter name to search" />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )}

            {/* Single user export */}
            {exportType === "single" && (
              <>
                <Alert
                  message="Single User Export"
                  description="Enter the User ID to export data for a specific user."
                  type="info"
                  showIcon
                  className="mb-4"
                />

                <Form.Item
                  label="User ID"
                  name="user_id"
                  rules={[
                    { required: true, message: "Please enter user ID" },
                    { pattern: /^\d+$/, message: "User ID must be a number" },
                  ]}
                >
                  <Input placeholder="Enter user ID (e.g., 123)" />
                </Form.Item>
              </>
            )}

            {/* All users export info */}
            {exportType === "all" && (
              <Alert
                message="Export All Users"
                description="This will export all users in the system (maximum 1000 users). No additional configuration needed."
                type="warning"
                showIcon
                className="mb-4"
              />
            )}

            <Divider />

            {/* Export Info */}
            <Card size="small" className="mb-4 bg-gray-50">
              <h4 className="font-semibold mb-2">📊 Export Information</h4>
              <Row gutter={16}>
                <Col span={12}>
                  <p className="text-sm mb-1">
                    <strong>Format:</strong> Excel (.xlsx)
                  </p>
                  <p className="text-sm mb-1">
                    <strong>Max Records:</strong> 1,000 users
                  </p>
                </Col>
                <Col span={12}>
                  <p className="text-sm mb-1">
                    <strong>Includes:</strong> All user fields
                  </p>
                  <p className="text-sm mb-1">
                    <strong>File Size:</strong> ~50KB per 100 users
                  </p>
                </Col>
              </Row>
            </Card>

            {/* Export Button */}
            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  loading={exportResponse.isLoading}
                  icon={<DownloadIcon className="h-4 w-4" />}
                  style={{
                    background: "linear-gradient(45deg, #f97316, #dc2626)",
                    border: "none",
                  }}
                >
                  {exportResponse.isLoading
                    ? "Preparing Export..."
                    : "Export Users"}
                </Button>

                <Button
                  onClick={() => form.resetFields()}
                  disabled={exportResponse.isLoading}
                >
                  Reset Filters
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>

        {/* Export Fields Info */}
        <Card title="📋 Export Fields" size="small">
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <h5>Personal Information:</h5>
              <ul className="text-sm space-y-1">
                <li>• User ID</li>
                <li>• First Name & Last Name</li>
                <li>• Email Address</li>
                <li>• Phone Number</li>
                <li>• Gotra & Gan</li>
              </ul>
            </Col>
            <Col xs={24} sm={12}>
              <h5>Account & Location:</h5>
              <ul className="text-sm space-y-1">
                <li>• Address Details</li>
                <li>• City, State, Pincode</li>
                <li>• Verification Status</li>
                <li>• Registration Date</li>
                <li>• Total Orders Count</li>
              </ul>
            </Col>
          </Row>
        </Card>
      </div>
    </>
  );
}

export default UserExport;
